<template>
  <div class="tiles">
    <div class="quick">
      <RouterLink
        :to="`/calendar?petId=${petId}`"
        class="quick__item"
      >
        <span class="quick__icon">
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linecap="round"
            stroke-linejoin="round"
          ><rect
            x="3"
            y="5"
            width="18"
            height="16"
            rx="3"
          /><path d="M8 3v4M16 3v4M3 10h18" /></svg>
        </span>
        <span class="quick__title">{{ t('petPassport.calendar') }}</span>
      </RouterLink>

      <div class="quick__item quick__item--disabled">
        <span class="quick__icon">
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linecap="round"
            stroke-linejoin="round"
          ><rect
            x="3"
            y="3"
            width="7"
            height="7"
            rx="1.5"
          /><rect
            x="14"
            y="3"
            width="7"
            height="7"
            rx="1.5"
          /><rect
            x="3"
            y="14"
            width="7"
            height="7"
            rx="1.5"
          /><path d="M14 14h3v3h-3zM20 14v.01M14 20h3M20 17v4" /></svg>
        </span>
        <span class="quick__title">{{ t('petPassport.qr') }}</span>
      </div>
    </div>

    <div class="grid">
      <a
        v-for="tile in tiles"
        :key="tile.key"
        class="tile"
        :class="{ 'tile--disabled': tile.disabled }"
        @click.prevent="tile.action && tile.action()"
      >
        <div class="tile__title">{{ tile.title }}</div>
        <div class="tile__status">
          <span
            class="tile__dot"
            :class="`tile__dot--${tile.tone}`"
          />
          <span class="tile__text">{{ tile.text }}</span>
        </div>
        <div
          v-if="tile.extra"
          class="tile__extra"
        >
          {{ tile.extra }}
        </div>
        <div class="tile__bottom">
          <span class="tile__icon">
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
              stroke-linejoin="round"
            ><path :d="tile.icon" /></svg>
          </span>
          <svg
            v-if="!tile.disabled"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linecap="round"
            stroke-linejoin="round"
          ><path d="M5 12h14M13 6l6 6-6 6" /></svg>
        </div>
      </a>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { getEvents } from '@/screens/EventsScreen/api'

const props = defineProps({
  pet: { type: Object, required: true },
  petId: { type: [String, Number], required: true },
})
const emit = defineEmits(['scroll-to'])

const { t } = useI18n()
const router = useRouter()

const nextEvent = ref(null)

const pad = (n) => String(n).padStart(2, '0')

const eventWhen = (event) => {
  const date = new Date(event.date)
  const today = new Date()
  const tomorrow = new Date()
  tomorrow.setDate(today.getDate() + 1)

  let day = date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })
  if (date.toDateString() === today.toDateString()) {day = t('calendarView.today')}
  if (date.toDateString() === tomorrow.toDateString()) {day = t('calendarView.tomorrow')}

  const time = event.all_day ? t('calendarView.allDay') : `${pad(date.getHours())}:${pad(date.getMinutes())}`
  return `${day} ${time}`
}

onMounted(async () => {
  const data = await getEvents()
  const startOfToday = new Date().setHours(0, 0, 0, 0)
  nextEvent.value = (data?.events || [])
    .filter((el) => Number(el.pet_id) === Number(props.petId) && new Date(el.date) >= startOfToday)
    .sort((a, b) => new Date(a.date) - new Date(b.date))[0] || null
})

const payload = computed(() => props.pet.payload || {})

const tiles = computed(() => {
  const vaccinations = payload.value.vaccinations || []
  const hasIlls = !!payload.value.chronicIlls

  return [
    {
      key: 'vac',
      title: t('petPassport.vaccinations'),
      tone: vaccinations.length ? 'green' : 'gray',
      text: vaccinations.length ? t('petPassport.records', { n: vaccinations.length }) : t('petPassport.noRecords'),
      icon: 'M18 2l4 4M17 7l3-3M19 9l-8.7 8.7a2.1 2.1 0 0 1-3 0L6.3 16.7a2.1 2.1 0 0 1 0-3L15 5M9 11l4 4M5 19l-3 3M14 4l6 6',
      action: () => emit('scroll-to', 'vac'),
    },
    {
      key: 'ills',
      title: t('petPassport.diseases'),
      tone: hasIlls ? 'orange' : 'green',
      text: hasIlls ? t('petPassport.hasRecords') : t('petPassport.allGood'),
      icon: 'M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1-1.1a5.5 5.5 0 0 0-7.8 7.8l1 1.1L12 21l7.8-7.5 1-1.1a5.5 5.5 0 0 0 0-7.8z',
      action: () => emit('scroll-to', 'ills'),
    },
    {
      key: 'events',
      title: t('petPassport.events'),
      tone: nextEvent.value ? 'orange' : 'gray',
      text: nextEvent.value ? nextEvent.value.title : t('petPassport.noEvents'),
      extra: nextEvent.value ? eventWhen(nextEvent.value) : '',
      icon: 'M3 8a3 3 0 0 1 3-3h12a3 3 0 0 1 3 3v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2zM8 3v4M16 3v4M3 11h18',
      action: () => router.push(`/calendar?petId=${props.petId}`),
    },
    {
      key: 'docs',
      title: t('petPassport.documents'),
      tone: 'gray',
      text: t('petPassport.soon'),
      icon: 'M6 2h9l5 5v13a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2zM14 2v6h6M8 13h8M8 17h8',
      disabled: true,
    },
  ]
})
</script>

<style lang="scss" scoped>
.tiles {
  display: grid;
  gap: 10px;
}

.quick {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;

  &__item {
    display: flex;
    align-items: center;
    height: 52px;
    border-radius: 26px;
    background: #fff;
    overflow: hidden;
    color: #26384b;
    text-decoration: none;

    &--disabled {
      opacity: 0.55;
    }
  }

  &__icon {
    flex: none;
    width: 52px;
    height: 52px;
    border-radius: 26px;
    background: #2a4a4f;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__title {
    flex: 1;
    text-align: center;
    padding-right: 8px;
    font-size: 14px;
    font-family: "NunitoBold";
  }
}

.grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.tile {
  display: flex;
  flex-direction: column;
  min-height: 150px;
  padding: 14px;
  border-radius: 22px;
  background: #fff;
  color: #26384b;
  cursor: pointer;
  min-width: 0;

  &--disabled {
    opacity: 0.55;
    cursor: default;
  }

  &__title {
    font-size: 16px;
    font-family: "NunitoBold";
  }

  &__status {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 8px;
    min-width: 0;
  }

  &__dot {
    flex: none;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: #b0b2c2;

    &--green {
      background: #8bc53f;
    }

    &--orange {
      background: #ff6b2c;
    }
  }

  &__text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 12px;
    font-family: "NunitoRegular";
    color: #8f98ab;
  }

  &__extra {
    margin-top: 2px;
    font-size: 12px;
    font-family: "NunitoBold";
  }

  &__bottom {
    margin-top: auto;
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
  }

  &__icon {
    width: 44px;
    height: 44px;
    border-radius: 14px;
    background: #f3f4f8;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

[theme="dark"] {
  .quick__item,
  .tile {
    background: #37363c;
    color: #fff;
  }

  .tile__icon {
    background: #292929;
  }
}
</style>
